import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { X, ChevronDown, Check } from 'lucide-react';
import { cn } from '@/lib/utils';

interface MultiSelectFilterProps {
  label: string;
  icon?: React.ReactNode;
  options: Array<{ id: string; label: string }>;
  selected: string[];
  onSelectionChange: (selected: string[]) => void;
  placeholder?: string;
  className?: string;
}

export function MultiSelectFilter({
  label,
  icon,
  options,
  selected,
  onSelectionChange,
  placeholder = 'Select...',
  className,
}: MultiSelectFilterProps) {
  const [open, setOpen] = useState(false);

  const handleToggle = (id: string) => {
    const newSelected = selected.includes(id)
      ? selected.filter(s => s !== id)
      : [...selected, id];
    onSelectionChange(newSelected);
  };

  const handleSelectAll = () => {
    if (selected.length === options.length) {
      onSelectionChange([]);
    } else {
      onSelectionChange(options.map(opt => opt.id));
    }
  };

  const handleClear = () => {
    onSelectionChange([]);
  };

  const selectedLabels = options
    .filter(opt => selected.includes(opt.id))
    .map(opt => opt.label);

  return (
    <div className={cn('space-y-2', className)}>
      <Label className="text-sm font-medium flex items-center gap-2">
        {icon}
        {label}
      </Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className="w-full justify-between h-10 text-left font-normal"
          >
            <span className="truncate">
              {selected.length === 0
                ? placeholder
                : selected.length === 1
                ? selectedLabels[0]
                : `${selected.length} selected`}
            </span>
            <ChevronDown className="h-4 w-4 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[300px] p-0" align="start">
          <div className="p-3 border-b">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm font-semibold">{label}</Label>
              {selected.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-xs"
                  onClick={handleClear}
                >
                  <X className="h-3 w-3 mr-1" />
                  Clear
                </Button>
              )}
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="w-full h-7 text-xs justify-start"
              onClick={handleSelectAll}
            >
              <Check className={cn(
                "h-3 w-3 mr-2",
                selected.length === options.length ? "opacity-100" : "opacity-0"
              )} />
              Select All ({options.length})
            </Button>
          </div>
          <div className="max-h-[300px] overflow-y-auto p-2">
            {options.length === 0 ? (
              <div className="text-sm text-muted-foreground text-center py-4">
                No options available
              </div>
            ) : (
              <div className="space-y-2">
                {options.map((option) => (
                  <div
                    key={option.id}
                    className="flex items-center space-x-2 p-2 rounded-md hover:bg-muted/50 cursor-pointer"
                    onClick={() => handleToggle(option.id)}
                  >
                    <Checkbox
                      id={option.id}
                      checked={selected.includes(option.id)}
                      onCheckedChange={() => handleToggle(option.id)}
                    />
                    <Label
                      htmlFor={option.id}
                      className="flex-1 text-sm font-normal cursor-pointer"
                    >
                      {option.label}
                    </Label>
                  </div>
                ))}
              </div>
            )}
          </div>
          {selected.length > 0 && (
            <div className="p-3 border-t">
              <div className="flex flex-wrap gap-1">
                {selectedLabels.slice(0, 3).map((label) => (
                  <Badge key={label} variant="secondary" className="text-xs">
                    {label}
                  </Badge>
                ))}
                {selected.length > 3 && (
                  <Badge variant="secondary" className="text-xs">
                    +{selected.length - 3} more
                  </Badge>
                )}
              </div>
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}

